import { useRef, useState, useMemo, useCallback, useEffect } from 'react'

import PropTypes from 'prop-types'
import { Alert, IconButton, Popover } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { v4 as uuid } from 'uuid'

import { dateFns, OPTIONS, today } from 'constants/index'
import ButtonGroup from '../ButtonGroup'

import {
  Calendar,
  Header,
  DateNavigation,
  DaysWrapper,
  Week,
  WeekdaysHeader,
  WeekdayLabel,
  Day,
  NavigationButton,
  PaperWrapper,
  PaperContent,
  PaperFooter,
  VerticalLine,
  Item,
  CustomTimeWrapper,
  TimeWrapper,
  ItemsWrapper,
} from './styles'
import { capitalize } from '../../helpers'
import { DateInput } from '../DateInput'
import LabelFilter from '../LabelFilter'

function DatePicker({ onChange, value, presetPeriods, disabled, validateRange }) {
  const [currentDateValue, setCurrentDateValue] = useState(() => {
    const [start = dateFns.startOfWeek(today), end = dateFns.endOfDay(start)] = value

    return [start, end]
  })

  const [currentDateView, setCurrentDateView] = useState(() => {
    const [start = today] = value

    return start
  })
  const [anchorEl, setAnchorEl] = useState(null)
  const clickCount = useRef(1)
  const [selectedPreset, setSelectedPreset] = useState('')

  useEffect(() => {
    if (value.length) {
      const [start, end = dateFns.endOfDay(start)] = value

      setCurrentDateValue([start, end])
      setCurrentDateView(start)
    }
  }, [value])

  const [startDate, endDate] = useMemo(() => currentDateValue, [currentDateValue])
  const weekArray = useMemo(() => dateFns.getWeekArray(currentDateView), [currentDateView])
  const currentYear = useMemo(() => dateFns.getYear(currentDateView), [currentDateView])
  const currentMonthText = useMemo(() => dateFns.format(currentDateView, 'month'), [currentDateView])

  const inputText = useMemo(() => {
    if (value.length) {
      const [start, end = start] = value
      // a preset label is only true while the range on screen is that preset (a refused request puts the old range back)
      const preset = presetPeriods.find(({ label }) => label === selectedPreset)
      if (preset && dateFns.isSameDay(start, preset.period[0]) && dateFns.isSameDay(end, preset.period[1])) {
        return selectedPreset
      }

      const startDateFormatted = dateFns.formatByString(start, 'dd MMM yyyy')
      const endDateFormatted = dateFns.formatByString(end, 'dd MMM yyyy')
      return `${startDateFormatted} - ${endDateFormatted}`
    }

    return ''
  }, [value, selectedPreset, presetPeriods])

  const isStartDateAfterEndDate = useMemo(() => dateFns.isAfter(startDate, endDate), [startDate, endDate])
  const maxAllowedEndDate = dateFns.addMonths(startDate, 12)
  const isBeforeJune2023 = useMemo(() => dateFns.isBefore(startDate, new Date(2023, 5, 1)), [startDate])
  const isAfterMay2023 = useMemo(() => dateFns.isAfter(endDate, new Date(2023, 5, 1)), [endDate])

  // Opt-in rule of the screen using the picker; kept apart from isDateValid so it cannot be bypassed by it.
  const rangeError = useMemo(() => validateRange?.([startDate, endDate]) ?? null, [validateRange, startDate, endDate])

  const handleOnChange = useCallback(() => {
    if (!isStartDateAfterEndDate && !rangeError) {
      const [start, end = dateFns.endOfDay(start)] = currentDateValue

      if (dateFns.isValid(start) && dateFns.isValid(end)) {
        onChange([start, end])
        setAnchorEl(null)
        clickCount.current = 1
      }
    }
  }, [isStartDateAfterEndDate, rangeError, onChange, currentDateValue])

  const joinDateAndTime = useCallback((date, timeDate) => {
    try {
      let currentDate = dateFns.setHours(date, dateFns.getHours(timeDate))
      currentDate = dateFns.setMinutes(currentDate, dateFns.getMinutes(timeDate))

      return currentDate
    } catch {
      return undefined
    }
  }, [])

  const isDateValid = useMemo(() => {
    return (
      (!isStartDateAfterEndDate &&
        (!maxAllowedEndDate || dateFns.isBefore(endDate, maxAllowedEndDate)) &&
        !isBeforeJune2023) ||
      !isAfterMay2023
    )
  }, [isStartDateAfterEndDate, maxAllowedEndDate, endDate, isBeforeJune2023, isAfterMay2023])

  return (
    <>
      <LabelFilter
        onClick={(event) => setAnchorEl(event.currentTarget)}
        value={inputText}
        data-testid="label-filter"
        disabled={disabled}
      />

      <Popover
        data-testid="popover-date-picker"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          square: true,
        }}
      >
        <PaperWrapper>
          <PaperContent>
            <ItemsWrapper>
              {presetPeriods.map(({ period: [startPeriod, endPeriod], label }) => (
                <Item
                  key={label}
                  onClick={() => {
                    setCurrentDateValue([startPeriod, endPeriod])
                    setCurrentDateView(endPeriod)
                    setSelectedPreset(label)
                  }}
                  isSelected={dateFns.isSameDay(startDate, startPeriod)}
                >
                  {label}
                </Item>
              ))}
              <TimeWrapper>
                <CustomTimeWrapper>
                  <DateInput
                    label="De:"
                    value={startDate}
                    onChange={(val) => {
                      setCurrentDateValue(([currentStartDate, currentEndDate]) => [
                        joinDateAndTime(val, currentStartDate),
                        currentEndDate,
                      ])
                      setSelectedPreset('')
                    }}
                    invalid={!isDateValid}
                    testId="datatime-input-start"
                  />
                </CustomTimeWrapper>
                <CustomTimeWrapper>
                  <DateInput
                    label="Até:"
                    value={endDate}
                    onChange={(val) => {
                      setCurrentDateValue(([currentStartDate, currentEndDate]) => [
                        currentStartDate,
                        joinDateAndTime(val, currentEndDate),
                      ])
                      setSelectedPreset('')
                    }}
                    invalid={!isDateValid}
                    testId="datatime-input-end"
                  />
                </CustomTimeWrapper>
              </TimeWrapper>
            </ItemsWrapper>
            <VerticalLine />
            <Calendar>
              <Header>
                <DateNavigation>
                  <p>
                    {capitalize(currentMonthText)} {currentYear}
                  </p>
                  <div>
                    <NavigationButton
                      onClick={() => setCurrentDateView((currentDate) => dateFns.addMonths(currentDate, -1))}
                    >
                      <ChevronLeft />
                    </NavigationButton>
                    <NavigationButton
                      onClick={() => setCurrentDateView((currentDate) => dateFns.addMonths(currentDate, 1))}
                    >
                      <ChevronRight />
                    </NavigationButton>
                  </div>
                </DateNavigation>

                <WeekdaysHeader>
                  {dateFns.getWeekdays().map((day, i) => (
                    <WeekdayLabel aria-hidden key={day + i.toString()} variant="caption">
                      {capitalize(day)}
                    </WeekdayLabel>
                  ))}
                </WeekdaysHeader>
              </Header>

              <DaysWrapper>
                {weekArray.map((week) => (
                  <Week key={uuid()}>
                    {week.map((day) => {
                      const isToday = dateFns.isSameDay(day, today)
                      const isSelected = dateFns.isSameDay(day, startDate) || dateFns.isSameDay(day, endDate)
                      const isAnotherMonth = !dateFns.isSameMonth(day, startDate)
                      const isBetweenDates =
                        !dateFns.isSameDay(startDate, endDate) &&
                        (dateFns.isEqual(day, startDate) || dateFns.isAfter(day, startDate)) &&
                        (dateFns.isEqual(day, endDate) || dateFns.isBefore(day, endDate))
                      const isDisabled =
                        dateFns.isAfterDay(day, today) ||
                        (dateFns.isBeforeDay(day, startDate) && clickCount.current % 2 === 0)
                      const firstDate = dateFns.isSameDay(day, startDate)
                      const lastDate = dateFns.isSameDay(day, endDate)

                      return (
                        <Day
                          key={day.getTime()}
                          isToday={isToday}
                          isSelected={isSelected}
                          isAnotherMonth={isAnotherMonth}
                          isBetweenDates={isBetweenDates}
                          rangeDate={[firstDate, lastDate]}
                        >
                          <IconButton
                            disabled={isDisabled}
                            onClick={() =>
                              setCurrentDateValue(([currentStartDate]) => {
                                let val = [dateFns.startOfDay(day)]

                                if (clickCount.current % 2 === 0) {
                                  val = [dateFns.startOfDay(currentStartDate), dateFns.endOfDay(day)]
                                }

                                clickCount.current += 1
                                setSelectedPreset('')
                                return val
                              })
                            }
                          >
                            {dateFns.format(day, 'dayOfMonth')}
                          </IconButton>
                        </Day>
                      )
                    })}
                  </Week>
                ))}
              </DaysWrapper>
            </Calendar>
          </PaperContent>
          {rangeError ? (
            <Alert
              severity="warning"
              data-testid="date-range-error"
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 0,
                width: 0,
                minWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              {rangeError}
            </Alert>
          ) : null}
          <PaperFooter>
            <ButtonGroup
              confirmDisabled={!isDateValid || Boolean(rangeError)}
              confirmAction={() => handleOnChange()}
              confirmLabel="Confirmar"
              cancelAction={() => {
                setAnchorEl(null)
                setCurrentDateValue(value)
              }}
              cancelLabel="cancelar"
            />
          </PaperFooter>
        </PaperWrapper>
      </Popover>
    </>
  )
}

DatePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  presetPeriods: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, period: PropTypes.arrayOf(PropTypes.instanceOf(Date)) }),
  ),
  disabled: PropTypes.bool,
  /** Opt-in: returns the reason a period cannot be confirmed, or null when it can */
  validateRange: PropTypes.func,
}

DatePicker.defaultProps = {
  value: [],
  presetPeriods: OPTIONS,
  disabled: false,
  validateRange: undefined,
}

export default DatePicker
